import React from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, isHovered }) => ({
  maxWidth: 400,
  borderRadius: 20,
  boxShadow: isHovered
    ? '0 12px 24px rgba(0, 0, 128, 0.4)'
    : '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  background: isHovered
    ? 'linear-gradient(to bottom, #000080 0%, #000000 100%) !important'  // Blue to black (top to bottom)
    : '#fff',
  color: isHovered ? '#fff' : '#000',
  border: isHovered ? '2px solid #6A0DAD' : '1px solid #e0e0e0',
  textAlign: 'center',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: 'auto',
  minHeight: 550,
  fontFamily: 'Poppins, sans-serif',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: isHovered
      ? 'linear-gradient(to right, #6A0DAD, #000080)'
      : 'transparent',
    transition: 'all 0.3s ease',
  },
}));

const PricingCardContent = ({ isHovered, plan }) => (
  <CardContent>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 700,
          color: isHovered ? '#fff' : '#000',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {plan.title.toUpperCase()}
      </Typography>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 600,
          color: isHovered ? '#fff' : '#000',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {plan.price}
      </Typography>
    </Box>

    <Button
      variant="contained"
      sx={{
        backgroundColor: isHovered ? '#fff' : '#000',
        color: isHovered ? '#000' : '#fff',
        textTransform: 'none',
        fontWeight: 700,
        padding: '12px 20px',
        borderRadius: 3,
        mb: 3,
        width: '80%',
        height: '50px',
        fontFamily: 'Poppins, sans-serif',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: isHovered ? '#e0e0e0' : '#333',
          transform: 'translateY(-2px)',
        },
      }}
    >
      Purchase Plan
    </Button>

    <Typography
      variant="subtitle1"
      sx={{
        mt: 2,
        mb: 2,
        fontWeight: 700,
        color: isHovered ? '#fff' : '#000000',
        opacity: isHovered ? 1 : 0.7,
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      {plan.package}
    </Typography>

    <Box sx={{ mt: 2 }}>
      {plan.features.map((feature, idx) => (
        <Typography
          key={idx}
          variant="body1"
          sx={{
            color: isHovered ? '#fff' : '#000000',
            textAlign: 'left',
            ml: 2,
            fontWeight: 500,
            opacity: isHovered ? 1 : 0.7,
            fontFamily: 'Poppins, sans-serif',
            transition: 'opacity 0.3s ease',
          }}
        >
          {feature}
        </Typography>
      ))}
    </Box>
  </CardContent>
);

const Pricing = () => {
  const [hoveredCard, setHoveredCard] = React.useState(null);

  const plans = [
    {
      title: 'lifetime',
      price: '$999',
      package: 'Elite Mastery Package (Advanced Level)',
      features: [
        'Features:',
        'Personalized 1-on-1 mentorship sessions',
        'Institutional-level trading insights and high-probability setups',
        'Psychological mastery and mindset coaching',
        'Exclusive high-impact trading strategies',
        'Private mastermind group with elite traders',
        'Lifetime VIP access to live trading sessions and real-time market analysis',
      ],
    },
    {
      title: 'yearly',
      price: '$299',
      package: 'Advanced Edge (Intermediate Level)',
      features: [
        'Features:',
        '1 year access to mentorship class',
        'Live trading session (2 times a week)',
        'Advanced technical analysis class',
        'Daily signals',
        'Advanced fundamental analysis class',
        'Mindset development class',
      ],
    },
    {
      title: 'monthly',
      price: '$99',
      package: 'FOUNDATION PACKAGE (Beginners to Advanced Level)',
      features: [
        'Features:',
        '1 month access to the mentorship class',
        'Live trading session (2 times a week)',
        'Daily signals',
        'Advanced top down analysis class',
        'Trading certificate',
      ],
    },
  ];

  return (
    <Box
      sx={{
        py: 8,
        textAlign: 'center',
        position: 'relative',
        backgroundColor: '#f9f9f9',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: '#000080',
          mb: 2,
          fontWeight: 600,
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        Packages
      </Typography>
      <Typography
        variant="h3"
        sx={{
          color: '#000',
          fontWeight: 600,
          mb: 4,
          letterSpacing: 1,
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        Affordable Pricing for every trader
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: '#000000',
          mb: 6,
          fontWeight: 600,
          opacity: 0.6,
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        PACKAGES (For Beginners & Advanced Traders)
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          flexWrap: 'wrap',
          px: 2,
        }}
      >
        {plans.map((plan, index) => (
          <StyledCard
            key={index}
            isHovered={hoveredCard === index}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <PricingCardContent isHovered={hoveredCard === index} plan={plan} />
          </StyledCard>
        ))}
      </Box>
    </Box>
  );
};

export default Pricing;